import React, { useCallback, useContext, useMemo, useState } from 'react';
import { KodplanContext } from '../../../../../../../Components/KodplanManager/Context/KodplanContext';
import KonteringContext from '../../../../../_Common/Components/KonteringManager/KonteringContext';
import { definedSelectedId } from '../../../../../../../Components/MasterDetail/MasterDetailUtils';
import { IColumnDef } from '../../../../../../../Types/ColumnDefType';
import { IBusinessCollectionProps } from '../../../../../../../Types/ObjektVy';
import {
  AssociationList,
  IAssociationListConfig,
} from '../../../../../_Common/Components/BusinessCollectionComponents/AssociationList/AssociationList';
import { useBusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import {
  getBokforderCustomColumns,
  getKontodelCustomConfigs,
  isRowEmpty,
  getDiffBetweenKreditDebet,
  getUsedKontodelarByBokforder,
} from '../../Utils/BokforderUtils';
import { GridContext } from '../../../../../../GridContext';
import { FormUpdateAssociatonType } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormValueUtils';
import { NEWLY_ADDED_ID_PREFIX_FOR_UPDATE } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Const/BusinessDataFormConst';
import BokforderContext from '../BokforderContext/BokforderContext';
import { ActiveDatabaseContext } from '../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { BokforderType__Associations_Rader } from '../../Queries/_QueryTypes/BokforderType';
import useRowFunctionMenuItems from '../../Hooks/useRowFunctionMenuItems';
import {
  TEDEKBokforderSTATUSEnum,
  TEDEKBokforderVariantEnum,
} from '../../../../../../../globalQueryTypes';
import { roundToTwoDecimals } from '../../../../../Leverantor/ObjektVyer/FakturaDel/Components/Kontering/Utils/KonteringUtils';
import { IValues } from '../../../../../_StandardRutin/Mutations/ObjectMutations/ObjectUpdateMutationBase';
import { IUserBokforderKodbehorigheter } from '../BokforderContext/BokforderContextManager';
import ImportDialog from '../ImportDialog/ImportDialog';
import { BokforderWrapperContext } from '../../../../Rutiner/Bokforder/Contexts/BokforderWrapperContext';
import { EKBokforderSysoptionQuery_db_SYSOptions_All } from '../../../../Rutiner/Bokforder/Queries/_QueryTypes/EKBokforderSysoptionQuery';
import { EK_USE_IMPORT_FROM_CLIPBOARD } from '../../../../Rutiner/Bokforder/Const/BokforderSysoptionConsts';
import {
  useBokforderImportFromClipboardOnCreateCallback,
  useBokforderImportFromClipboardSelectableColumnHeaders,
} from './Utils/BokforderGridUtils';
import { isCellDisabledBfo } from '../../Utils/BfoUtanordningUtils';
import EDAvenyProgress from '../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';

interface IProps extends IBusinessCollectionProps {
  editKontering: boolean;
  setEditKontering: React.Dispatch<React.SetStateAction<boolean>>;
}

interface IBokfOrderCustomValues {
  lastUpdatedBokfOrderRad?: string;
}

export const BokforderGridView: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { userID } = useContext(ActiveDatabaseContext);
  const bokforderContext = useContext(BokforderContext);
  const bokforderWrapperContext = useContext(BokforderWrapperContext);
  const formContext = useBusinessDataFormContext<
    // BokforderQuery_node_TEDEKBokforder,
    any,
    IBokfOrderCustomValues
  >();
  const konteringContext = useContext(KonteringContext);
  const kodplaner = useContext(KodplanContext);
  const bokforderContextMakeNewRow = bokforderContext.makeNewRow;
  const bokforderContextSetNewRowIndex = bokforderContext.setNewRowIndex;
  const bokforderContextSetImportDialogOpen =
    bokforderContext.setImportDialogOpen;
  const formContextUpdate = formContext?.update;

  const [templateHasBeenInit, setTemplateHasBeenInit] = useState(false);

  const selectedTemplate = useMemo(() => {
    return bokforderContext.templates?.find(
      template =>
        template.id === formContext?.data?._Associations?.Bokforingsmall?.id
    );
  }, [
    bokforderContext.templates,
    formContext?.data?._Associations?.Bokforingsmall?.id,
  ]);

  const ekUseImportFromClipboard: boolean = useMemo(() => {
    return (
      bokforderWrapperContext.sysOptions?.find(
        (option: EKBokforderSysoptionQuery_db_SYSOptions_All) =>
          option?.OptionKey === EK_USE_IMPORT_FROM_CLIPBOARD
      )?.OptionData === 'Y'
    );
  }, [bokforderWrapperContext.sysOptions]);

  const attestRows: BokforderType__Associations_Rader[] = useMemo(
    () =>
      formContext?.data?._Associations?.Rader?.filter(
        (row: any) => row?._Associations?.Attestant?.id === userID
      ) ?? [],
    [formContext?.data?._Associations?.Rader, userID]
  );

  const rowHasReserv = useMemo(
    () =>
      formContext?.data?._Associations?.Rader?.some(
        (row: BokforderType__Associations_Rader) =>
          row._Associations?.Reserv != null
      ),
    [formContext?.data?._Associations?.Rader]
  );

  const haveAttesterat = useMemo(
    () => attestRows.every(row => row.ATTESTERAD_DATUM != null),
    [attestRows]
  );

  const createNewRow = useCallback(
    (index: number, newValues?: IValues) => {
      formContextUpdate?.(
        bokforderContextMakeNewRow(
          `${NEWLY_ADDED_ID_PREFIX_FOR_UPDATE}Bokforder_Rad_${index}`,
          newValues
        ),
        true
      );
    },
    [formContextUpdate, bokforderContextMakeNewRow]
  );

  const lastRow: BokforderType__Associations_Rader = useMemo(() => {
    return (
      formContext?.formState.currentValues?._Associations?.Rader?.[
        formContext?.formState.currentValues?._Associations?.Rader?.length - 1
      ] ?? {}
    );
  }, [formContext?.formState.currentValues?._Associations?.Rader]);

  const lastEditedRow: BokforderType__Associations_Rader = useMemo(() => {
    return formContext?.formState.currentValues?._Associations?.Rader?.find(
      (row: BokforderType__Associations_Rader) =>
        row.id === formContext?.formState.customValues?.lastUpdatedBokfOrderRad
    );
  }, [
    formContext?.formState.currentValues?._Associations?.Rader,
    formContext?.formState.customValues?.lastUpdatedBokfOrderRad,
  ]);

  const lastEditedNonTemplateRow = useMemo(() => {
    if (
      lastEditedRow &&
      lastEditedRow?.MALLRAD_LOPNR == null &&
      bokforderContext.templateRows.length > 0 &&
      !bokforderContext.templateRows.some(
        templateRow => templateRow.id === lastEditedRow.id
      )
    ) {
      return lastEditedRow;
    } else {
      return null;
    }
  }, [bokforderContext.templateRows, lastEditedRow]);

  const haveOnlyEditOneNonTemplateRow = useMemo(() => {
    if (
      formContext?.formState?.currentValues != null &&
      formContext?.formState.currentValues?._Associations != null &&
      formContext?.formState.currentValues?._Associations?.Rader != null
    ) {
      const nonTemplateRows: BokforderType__Associations_Rader[] =
        formContext?.formState.currentValues?._Associations?.Rader.reduce(
          (
            prev: BokforderType__Associations_Rader[],
            row: BokforderType__Associations_Rader
          ) => {
            if (
              !bokforderContext.templateRows.some(
                templateRow => templateRow.id === row.id
              ) &&
              row.MALLRAD_LOPNR == null
            ) {
              return [...prev, row];
            } else {
              return prev;
            }
          },
          []
        );
      let changedOnlyOneRow = false;

      for (let index = 0; index < nonTemplateRows.length; index++) {
        const nonTemplateRow = nonTemplateRows[index];

        //Denna lösningen nollställer när det skapas nya tom rad efter att man har redigerat en rad.
        if (!isRowEmpty(nonTemplateRow) && !changedOnlyOneRow) {
          changedOnlyOneRow = true;
        } else {
          changedOnlyOneRow = false;
        }
      }
      return changedOnlyOneRow;
    } else {
      return false;
    }
  }, [bokforderContext.templateRows, formContext?.formState.currentValues]);

  const allRows: BokforderType__Associations_Rader[] =
    formContext?.formState.currentValues?._Associations?.Rader ?? [];

  const debKredDiff = getDiffBetweenKreditDebet(allRows);

  const createNewEmptyRow = useCallback(() => {
    createNewRow(bokforderContext.newRowIndex);
    bokforderContextSetNewRowIndex(prevIndex => prevIndex + 1);
    const rows: BokforderType__Associations_Rader[] | null =
      formContext?.formState.currentValues?._Associations?.Rader;

    if (
      (Object.keys(lastRow).length >= 0 || formContext?.isNew) &&
      rows == undefined &&
      bokforderContext.bokforderVariant === TEDEKBokforderVariantEnum.TOM &&
      formContext?.formState.currentValues?.MinBokforderStatus == undefined
    ) {
      //Det är en ny bokforder utan några rader
      createNewRow(bokforderContext.newRowIndex + 1);
      bokforderContextSetNewRowIndex(prevIndex => prevIndex + 2);
    }
  }, [
    bokforderContext.bokforderVariant,
    bokforderContext.newRowIndex,
    bokforderContextSetNewRowIndex,
    createNewRow,
    formContext?.formState.currentValues?.MinBokforderStatus,
    formContext?.formState.currentValues?._Associations?.Rader,
    formContext?.isNew,
    lastRow,
  ]);

  //Skapa nya tomma rader
  if (
    (Object.keys(lastRow).length >= 0 || formContext?.isNew) &&
    !kodplaner.loading &&
    kodplaner.activeKodplan &&
    !formContext?.formState.loading &&
    (bokforderContext.isEditMode || props.editKontering) &&
    bokforderContext.bokforderVariant !== TEDEKBokforderVariantEnum.PERIODISERA
  ) {
    if (
      bokforderContext.bokforderVariant === TEDEKBokforderVariantEnum.VAND ||
      bokforderContext.bokforderVariant === TEDEKBokforderVariantEnum.OMBOKFOR
    ) {
      if (debKredDiff !== 0 && !isRowEmpty(lastRow, true)) {
        createNewEmptyRow();
      }
    } else {
      if (!isRowEmpty(lastRow)) {
        createNewEmptyRow();
      }
    }
  }

  //Kredit/Debet hjälp
  if (
    (bokforderContext.bokforderVariant === TEDEKBokforderVariantEnum.TOM &&
      formContext?.formState.customValues?.lastUpdatedBokfOrderRad !== '') ||
    bokforderContext.bokforderVariant === TEDEKBokforderVariantEnum.OMBOKFOR ||
    bokforderContext.bokforderVariant === TEDEKBokforderVariantEnum.VAND ||
    bokforderContext.bokforderVariant === TEDEKBokforderVariantEnum.KOPIERA ||
    (bokforderContext.bokforderVariant === TEDEKBokforderVariantEnum.MALL &&
      lastEditedNonTemplateRow !== null)
  ) {
    if (lastEditedRow?.REF_LOPNR == null) {
      if (
        lastRow.id !==
        formContext?.formState.customValues?.lastUpdatedBokfOrderRad
      ) {
        //användaren ändrar inte på den sista raden
        if (debKredDiff !== 0) {
          let newValue = roundToTwoDecimals(debKredDiff);

          if (lastRow?.KREDIT > 0) {
            newValue -= lastRow.KREDIT;
          }
          if (lastRow.DEBET >= 0) {
            newValue += lastRow.DEBET;
          }

          if (newValue !== 0) {
            if (!isRowEmpty(lastRow)) {
              // Skapa ny rad om man tar bort sista raden och det finns kvar en diff i debet/kredit
              createNewRow(bokforderContext.newRowIndex, {
                DEBET: debKredDiff >= 0 ? Math.abs(debKredDiff) : null,
                KREDIT: debKredDiff < 0 ? Math.abs(debKredDiff) : null,
              });
              bokforderContext.setNewRowIndex(bokforderContext.newRowIndex + 1);
            } else {
              const values: FormUpdateAssociatonType = {
                id: lastRow.id,
                associationName: 'Rader',
                operation: lastRow.id.includes(NEWLY_ADDED_ID_PREFIX_FOR_UPDATE)
                  ? 'Add_Values'
                  : 'Update_Values',
                values: {
                  DEBET: newValue >= 0 ? Math.abs(newValue) : null,
                  KREDIT: newValue < 0 ? Math.abs(newValue) : null,
                },
              };

              formContext?.update([values], true);
            }
          } else if (newValue === 0 && lastRow.id != undefined) {
            // Resbeloppet är = 0 => sista raden rensas på kredit/debet
            const values: FormUpdateAssociatonType = {
              id: lastRow.id,
              associationName: 'Rader',
              operation: lastRow.id.includes(NEWLY_ADDED_ID_PREFIX_FOR_UPDATE)
                ? 'Add_Values'
                : 'Update_Values',
              values: {
                DEBET: null,
                KREDIT: null,
              },
            };
            formContext?.update([values], true);
          }
        }
      } else if (
        lastRow.id ===
        formContext?.formState.customValues?.lastUpdatedBokfOrderRad
      ) {
        //Användaren ändrar på den sista raden
        if (debKredDiff !== 0) {
          if (
            isRowEmpty(lastRow, false) ||
            (haveOnlyEditOneNonTemplateRow &&
              bokforderContext.bokforderVariant ===
                TEDEKBokforderVariantEnum.MALL &&
              formContext.isNew)
          ) {
            const values: FormUpdateAssociatonType = {
              id: lastRow.id,
              associationName: 'Rader',
              operation: lastRow.id.includes(NEWLY_ADDED_ID_PREFIX_FOR_UPDATE)
                ? 'Add_Values'
                : 'Update_Values',
              values: {
                DEBET: debKredDiff >= 0 ? Math.abs(debKredDiff) : null,
                KREDIT: debKredDiff < 0 ? Math.abs(debKredDiff) : null,
              },
            };
            formContext?.update([values], true);
          } else {
            createNewRow(bokforderContext.newRowIndex, {
              DEBET: debKredDiff >= 0 ? Math.abs(debKredDiff) : null,
              KREDIT: debKredDiff < 0 ? Math.abs(debKredDiff) : null,
            });
            bokforderContext.setNewRowIndex(bokforderContext.newRowIndex + 1);
          }
        }
      }
    }
  }

  if (
    !templateHasBeenInit &&
    !konteringContext.konteringValidationQueryLoading &&
    selectedTemplate
  ) {
    setTemplateHasBeenInit(true);

    bokforderContext.makeTemplateRowsArray(
      selectedTemplate,
      formContext?.data?._Associations?.Rader
    );
  }

  const usersWithKodbehorigheter: IUserBokforderKodbehorigheter[] = useMemo(
    () => bokforderContext.allBokforderUsersWithKodbeh,
    [bokforderContext.allBokforderUsersWithKodbeh]
  );

  const id = props.id ? props.id : '';

  const bokforderConfig: IAssociationListConfig = useMemo(() => {
    const kontodelDefFromKodplan =
      konteringContext.konteringUtils.getKontodelDefsByKodplanId(
        bokforderContext.bokforderKodplan?.id ?? ''
      );

    const usedKontodelar = getUsedKontodelarByBokforder(
      formContext?.data?._Associations
        ?.Rader as BokforderType__Associations_Rader[]
    );

    const isAttest =
      formContext?.data?.STATUS === TEDEKBokforderSTATUSEnum.VANTAR_PA_ATTEST &&
      bokforderContext.isAttestant;

    const isReservAttest =
      formContext?.data?.STATUS === TEDEKBokforderSTATUSEnum.VANTAR_PA_ATTEST &&
      bokforderContext.isReservForAttestant;

    const allowedStatuses = [
      TEDEKBokforderSTATUSEnum.UTKAST,
      TEDEKBokforderSTATUSEnum.EJ_GODKAND,
      TEDEKBokforderSTATUSEnum.VANTAR_PA_ATTEST,
    ];

    const isReservAndHaveAttesterat = isReservAttest
      ? bokforderContext.everyReservAttesterIsAttesterade
      : !allowedStatuses.includes(formContext?.data?.STATUS) &&
        !formContext?.isNew;

    const attestantHaveAttesterat = isAttest
      ? haveAttesterat
      : !allowedStatuses.includes(formContext?.data?.STATUS) &&
        !formContext?.isNew;

    const editSwitchTurnedOff =
      !bokforderContext.showAttestantKonteraSwitch ||
      (bokforderContext.showAttestantKonteraSwitch && !props.editKontering);

    const disableEdit =
      (attestantHaveAttesterat && isReservAndHaveAttesterat) ||
      ((isAttest || isReservAttest) && editSwitchTurnedOff);

    const customDefaultColumns: IColumnDef[] = getBokforderCustomColumns(
      kontodelDefFromKodplan,
      bokforderContext.bokforderDate,
      usersWithKodbehorigheter,
      disableEdit,
      bokforderContext.bokforderKodplan?.id ?? '',
      konteringContext.konteringUtils,
      bokforderContext.templateRows,
      usedKontodelar,
      rowHasReserv,
      false,
      '',
      undefined,
      bokforderContext.showAttestantKonteraSwitch
    );

    const kontodelCustomConfigs = getKontodelCustomConfigs(
      kontodelDefFromKodplan
    );

    return {
      inCard: true,
      integrated:
        (isAttest || isReservAttest || bokforderContext.isEditMode) ?? false,
      defaultVisningstyp: 'tabell',
      customDefaultColumns: customDefaultColumns,
      useFunctionMenuItems: input =>
        useRowFunctionMenuItems(input, {
          disabled: editSwitchTurnedOff,
        }),
      customBusinessConfig: {
        associations: kontodelCustomConfigs,
      },
      sumColumns: [{ columnName: 'DEBET' }, { columnName: 'KREDIT' }],
      isCellDisabled: params =>
        isCellDisabledBfo(
          params,
          formContext?.data?.STATUS,
          bokforderContext.showAttestantKonteraSwitch,
          props.editKontering,
          bokforderContext.bokforderVariant,
          bokforderContext.templateRows
        ),
    };
  }, [
    konteringContext.konteringUtils,
    bokforderContext.bokforderKodplan?.id,
    bokforderContext.isAttestant,
    bokforderContext.isReservForAttestant,
    bokforderContext.everyReservAttesterIsAttesterade,
    bokforderContext.showAttestantKonteraSwitch,
    bokforderContext.bokforderDate,
    bokforderContext.templateRows,
    bokforderContext.isEditMode,
    bokforderContext.bokforderVariant,
    formContext?.data?.STATUS,
    formContext?.data?._Associations?.Rader,
    formContext?.isNew,
    haveAttesterat,
    props.editKontering,
    usersWithKodbehorigheter,
    rowHasReserv,
  ]);

  const selectableColumnHeaders =
    useBokforderImportFromClipboardSelectableColumnHeaders(true);

  const onImportCreateCallback =
    useBokforderImportFromClipboardOnCreateCallback();

  return (
    <>
      {formContext?.formState.loading ||
      konteringContext.konteringValidationQueryLoading ? (
        <>
          <EDAvenyProgress />
        </>
      ) : (
        <EDBox marginTop={0.8}>
          <GridContext.Provider value={{ inGrid: true }}>
            <AssociationList
              associationName="Rader"
              associationPath="Rader"
              collectionName={'EKBokforder'}
              ownId={definedSelectedId(id)}
              config={bokforderConfig}
            />
          </GridContext.Provider>
        </EDBox>
      )}

      {ekUseImportFromClipboard && (
        <ImportDialog
          associationName="Rader"
          collectionName="EKBokforder"
          open={bokforderContext.importDialogOpen}
          selectableColumnHeaders={selectableColumnHeaders}
          setOpen={bokforderContextSetImportDialogOpen}
          onCreateCallback={onImportCreateCallback}
          invalidValuesInfoText="Orangemarkerade värden kommer ej att läsas in i bokföringsordern"
        />
      )}
    </>
  );
};
